import { Link } from 'react-router'
import { SEO } from '@carouselgg/nexus/seo'
import { AngledBand } from '@carouselgg/nexus/angled-band'
import { DisplayHeading } from '@carouselgg/nexus/display-heading'
import { GradientText } from '@carouselgg/nexus/gradient-text'
import { ScrollReveal } from '@carouselgg/nexus/scroll-reveal'
import { FeatureCard } from '@carouselgg/nexus/feature-card'
import {
  Globe,
  Settings,
  ShieldCheck,
  Trophy,
  Building2,
  GraduationCap,
  Users,
  DiscordLogo,
  RiotLogo,
  SteamLogo,
  XboxLogo,
  PlayStationLogo,
  TwitchLogo,
  YouTubeLogo,
  BattleNetLogo,
} from '@carouselgg/nexus/icon'
import styles from './HowItWorksPage.module.css'

const pillars = [
  {
    Icon: Globe,
    title: 'Discovery',
    body: 'Find leagues, teams and players by game, region and skill level. No more word-of-mouth hunting through Discord servers.',
    accent: 'var(--nexus-purple)',
  },
  {
    Icon: Settings,
    title: 'Operations',
    body: 'Leagues run seasons, scheduling, standings and rosters in one place — instead of a spreadsheet and three Discord bots.',
    accent: 'var(--nexus-blue)',
  },
  {
    Icon: ShieldCheck,
    title: 'Identity',
    body: 'One verified gaming identity across every platform, with Elo pulled from game APIs. No smurfs, no alts, no sandbagging.',
    accent: 'var(--nexus-green)',
  },
]

const tracks = [
  {
    label: 'For players',
    Icon: Users,
    accent: 'var(--nexus-pink)',
    steps: [
      { number: '01', title: 'Link your accounts', description: 'Connect Riot, Steam, Xbox, PlayStation, Discord and more. One verified identity across all platforms.' },
      { number: '02', title: 'Find your league', description: 'Browse leagues by game, region and skill level. Join a team or get recruited.' },
      { number: '03', title: 'Compete and climb', description: 'Play verified matches, build your competitive history, and get scouted.' },
    ],
  },
  {
    label: 'For league organizers',
    Icon: Trophy,
    accent: 'var(--nexus-purple)',
    steps: [
      { number: '01', title: 'Create your league', description: 'Set up your league, define rules and choose your games.' },
      { number: '02', title: 'Launch a season', description: 'Configure divisions, set schedules and open registration. Players find you through Carousel.' },
      { number: '03', title: 'Manage on autopilot', description: 'Automated standings, match management and verified results. Focus on your community.' },
    ],
  },
  {
    label: 'For clubs & orgs',
    Icon: Building2,
    accent: 'var(--nexus-blue)',
    steps: [
      { number: '01', title: 'Create your org', description: 'Set up your club in minutes. Add branding, recruit players, manage teams.' },
      { number: '02', title: 'Build your roster', description: 'Create teams across games and Elo brackets. One hub for your entire org.' },
      { number: '03', title: 'Join leagues', description: 'Browse leagues, register your teams and compete. Your org page tracks it all.' },
    ],
  },
  {
    label: 'For universities',
    Icon: GraduationCap,
    accent: 'var(--nexus-green)',
    steps: [
      { number: '01', title: 'Register your program', description: 'Onboard your esports program with academic integrations and roster tools.' },
      { number: '02', title: 'Manage eligibility', description: 'Track student eligibility, manage rosters across games and stay compliant.' },
      { number: '03', title: 'Scout and develop', description: 'Scouting reports, competitive history and a scholarship pipeline — all verified.' },
    ],
  },
]

/**
 * Mirrors api/internal/auth/trust_score.go `levelFromScore`.
 *
 * The previous ranges here were wrong — they showed Unverified as 0–9 and Basic
 * as 10–19, which told a player with a score of 5 they were Unverified when the
 * API considers them Basic. Only a score of exactly 0 is Unverified.
 */
const trustLevels = [
  { level: 'Unverified', range: '0', color: 'var(--nexus-text-muted)' },
  { level: 'Basic', range: '1–19', color: 'var(--nexus-text-secondary)' },
  { level: 'Verified', range: '20–49', color: 'var(--nexus-blue)' },
  { level: 'Established', range: '50–79', color: 'var(--nexus-green)' },
  { level: 'Veteran', range: '80–100', color: 'var(--nexus-purple)' },
]

const platforms = [
  { Icon: RiotLogo, name: 'Riot Games' },
  { Icon: SteamLogo, name: 'Steam' },
  { Icon: DiscordLogo, name: 'Discord' },
  { Icon: XboxLogo, name: 'Xbox' },
  { Icon: PlayStationLogo, name: 'PlayStation' },
  { Icon: BattleNetLogo, name: 'Battle.net' },
  { Icon: TwitchLogo, name: 'Twitch' },
  { Icon: YouTubeLogo, name: 'YouTube' },
]

export function HowItWorksPage() {
  return (
    <>
      <SEO
        title="How Carousel works"
        description="How Carousel runs organized amateur esports: verified gaming identity, a Trust Score that catches smurfs, and league operations that do not run on spreadsheets."
      />

      {/* Hero */}
      <AngledBand tone="base" slice="bottom">
        <div className={styles.inner}>
          <div className={styles.hero}>
            <p className={styles.eyebrow}>How it works</p>
            <DisplayHeading as="h1" size="7xl">
              How Carousel{' '}
              <br />
              <GradientText gradient="brand" angle={115}>works.</GradientText>
            </DisplayHeading>
            <p className={styles.lede}>
              285 million gamers in North America, and no organized amateur infrastructure. Players
              cannot find leagues. Organizers run everything on spreadsheets. Smurfs kill leagues
              before they grow. This is how we fix all three.
            </p>
          </div>
        </div>
      </AngledBand>

      {/* Pillars */}
      <AngledBand tone="elevated" slice="both" reverse>
        <div className={styles.inner}>
          <ScrollReveal>
            <DisplayHeading size="5xl" className={styles.sectionHead}>
              Three problems, one platform.
            </DisplayHeading>
          </ScrollReveal>
          <div className={styles.pillars}>
            {pillars.map((pillar, index) => (
              <ScrollReveal key={pillar.title} delay={index * 80}>
                <FeatureCard
                  icon={<pillar.Icon size={26} />}
                  title={pillar.title}
                  description={pillar.body}
                  accent={pillar.accent}
                  size="lg"
                />
              </ScrollReveal>
            ))}
          </div>
        </div>
      </AngledBand>

      {/* Tracks */}
      <AngledBand tone="base" slice="both">
        <div className={styles.inner}>
          <ScrollReveal>
            <DisplayHeading size="5xl" className={styles.sectionHead}>Get started</DisplayHeading>
          </ScrollReveal>

          {tracks.map((track) => (
            <ScrollReveal key={track.label}>
              <div className={styles.track} style={{ ['--track-accent' as string]: track.accent }}>
                <h3 className={styles.trackLabel}>
                  <track.Icon size={20} />
                  {track.label}
                </h3>
                <div className={styles.steps}>
                  {track.steps.map((step) => (
                    <div key={step.number} className={styles.step}>
                      <span className={styles.stepNumber}>{step.number}</span>
                      <div>
                        <h4 className={styles.stepTitle}>{step.title}</h4>
                        <p className={styles.stepBody}>{step.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </ScrollReveal>
          ))}
        </div>
      </AngledBand>

      {/* Trust Score */}
      <AngledBand id="trust-score" tone="elevated" slice="both" reverse>
        <div className={styles.inner}>
          <ScrollReveal>
            <DisplayHeading size="5xl" className={styles.sectionHead}>Trust Score</DisplayHeading>
            <p className={styles.sectionBody}>
              Your Trust Score (0–100) measures how verified your gaming identity is. It is built
              from identity verification, linked gaming accounts, real game data, social accounts
              and account maturity. A higher score proves you are who you say you are — and unlocks
              access to competitive leagues.
            </p>
          </ScrollReveal>
          <ScrollReveal delay={80}>
            <div className={styles.trustLevels}>
              {trustLevels.map((level) => (
                <div key={level.level} className={styles.trustLevel}>
                  <span className={styles.trustDot} style={{ background: level.color }} />
                  <span className={styles.trustName}>{level.level}</span>
                  <span className={styles.trustRange}>{level.range}</span>
                </div>
              ))}
            </div>
          </ScrollReveal>
        </div>
      </AngledBand>

      {/* Platforms + CTA */}
      <AngledBand tone="base" slice="top">
        <div className={styles.inner}>
          <ScrollReveal>
            <DisplayHeading size="5xl" className={styles.sectionHead}>
              Supported platforms
            </DisplayHeading>
            <div className={styles.platforms}>
              {platforms.map(({ Icon, name }) => (
                <div key={name} className={styles.platform}>
                  <Icon size={30} />
                  <span className={styles.platformName}>{name}</span>
                </div>
              ))}
            </div>
          </ScrollReveal>

          <ScrollReveal>
            <div className={styles.cta}>
              <DisplayHeading size="6xl">Ready to jump in?</DisplayHeading>
              <div className={styles.ctaButtons}>
                <Link to="/compete" className={styles.primaryCta}>Compete</Link>
                <Link to="/create-org" className={styles.secondaryCta}>Create an Org</Link>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </AngledBand>
    </>
  )
}
